/**
 * FIN-13190 — clean a rule tree's function arguments before it is emitted.
 *
 * Two things the query builder puts in `properties.field.args` that the backend cannot decode:
 *
 *  1. **Unset operand slots.** RAQB materialises an argument the analyst has not filled as
 *     `{valueSrc: 'field'}` — the object is present, the `value` key is not. `ArgElement`'s
 *     decoder reads `value` unconditionally, so the whole rule came back
 *     `Missing required field` (HTTP 422) and nothing could be saved.
 *  2. **Arguments left over from a previously selected function.** Switching a rule's function
 *     keeps the old argument names alongside the new ones, so a Subtract→Sum switch posts
 *     `{minuend, subtrahend, arg1, arg2}`. That one is the dangerous half: `number.SUM` decodes
 *     every key it is given, so the same two operands would have been **added twice** with no
 *     error anywhere.
 *
 * Sanitising on the way out (rather than filtering inside each `formatFunc`) also repairs rules
 * that were already persisted in the broken shape: they are cleaned the next time the variant is
 * saved.
 */

/** Arg names a function declares, or null when the function is not in the picker config. */
export const funcArgKeys = (config, funcPath) => {
  if (!config?.funcs || typeof funcPath !== 'string') return null;
  const separator = config.settings?.fieldSeparator || '.';
  const [group, ...rest] = funcPath.split(separator);
  let node = config.funcs[group];
  rest.forEach((part) => {
    node = node?.subfields?.[part];
  });
  return node?.args ? Object.keys(node.args) : null;
};

/** `arg1`, `arg2`, … — the shape the query builder gives a commutative function's operands. */
const isNumberedArg = (key) => /^arg\d+$/.test(key);

const argIndex = (key) => parseInt(key.replace('arg', ''), 10);

/**
 * True when the function numbers its own operands (`SUM`, `MULTIPLY`) rather than naming them
 * (`SUBTRACT`'s `minuend`/`subtrahend`). Only for those is an undeclared `argN` plausibly a
 * surplus operand of this same function; on any other function it is a stale key left behind by
 * whichever function was selected before.
 */
const declaresNumberedArgs = (declared) =>
  !!declared?.length && declared.every(isNumberedArg);

const hasValue = (arg) =>
  arg &&
  typeof arg === 'object' &&
  arg.value !== undefined &&
  arg.value !== null &&
  arg.value !== '';

/**
 * A `field` is a function reference when it is an object carrying `func`; a plain field is a
 * string. Returns the value unchanged for anything that is not a function.
 */
export const sanitizeFuncValue = (funcValue, config) => {
  if (!funcValue || typeof funcValue !== 'object' || !funcValue.func)
    return funcValue;

  const declared = funcArgKeys(config, funcValue.func);
  const args = {};

  Object.entries(funcValue.args || {}).forEach(([key, arg]) => {
    if (declared && !declared.includes(key)) {
      // An undeclared arg is either a stale key from whichever function was selected before
      // this one (`minuend` on a SUM) or a surplus operand of THIS function from before the
      // arity change (`arg3` on a five-factor MULTIPLY). Dropping the first is the point;
      // dropping the second would silently change what the rule computes, so a populated
      // numbered arg is kept and left for the backend, which decodes any number of them.
      // `migrateFuncArity` folds those into nested form at load, so this is a backstop for a
      // tree that reached the builder some other way.
      if (
        !(declaresNumberedArgs(declared) && isNumberedArg(key) && hasValue(arg))
      )
        return;
      args[key] = arg;
      return;
    }
    if (!arg || typeof arg !== 'object') return;

    if (arg.valueSrc === 'func') {
      const nested = sanitizeFuncValue(arg.value, config);
      // A nested function with no operands left is not worth emitting.
      if (!nested?.func || !Object.keys(nested.args || {}).length) return;
      args[key] = { ...arg, value: nested };
      return;
    }

    if (!hasValue(arg)) return;
    args[key] = arg;
  });

  return { ...funcValue, args };
};

/** Arg names the function declares but the rule has not filled in (recursively). */
export const missingFuncArgs = (funcValue, config) => {
  if (!funcValue || typeof funcValue !== 'object' || !funcValue.func) return [];

  const declared = funcArgKeys(config, funcValue.func);
  if (!declared) return [];

  return declared.reduce((missing, key) => {
    const arg = funcValue.args?.[key];
    if (arg?.valueSrc === 'func') {
      return missing.concat(missingFuncArgs(arg.value, config));
    }
    return hasValue(arg) ? missing : missing.concat(`${funcValue.func}.${key}`);
  }, []);
};

/** Walks a plain-JS RAQB tree and sanitises every function-valued `field`. */
export const sanitizeFuncArgs = (node, config) => {
  if (!node || typeof node !== 'object') return node;

  const next = { ...node };

  if (node.properties?.field && typeof node.properties.field === 'object') {
    next.properties = {
      ...node.properties,
      field: sanitizeFuncValue(node.properties.field, config)
    };
  }

  // children1 is an array in stored JSON and an object once RAQB has round-tripped it.
  if (node.children1 && typeof node.children1 === 'object') {
    next.children1 = Array.isArray(node.children1)
      ? node.children1.map((child) => sanitizeFuncArgs(child, config))
      : Object.entries(node.children1).reduce((acc, [key, child]) => {
          acc[key] = sanitizeFuncArgs(child, config);
          return acc;
        }, {});
  }

  return next;
};

/**
 * FIN-13190 — fold a function's surplus operands into nested form when the tree is loaded.
 *
 * `SUM` and `MULTIPLY` used to declare `arg1..arg5`. A rule saved back then can hold up to five
 * operands, and `Utils.checkTree` drops every argument the config does not declare — silently,
 * at load, before anything else runs. So simply cutting the arity to two would have made opening
 * a five-factor rule and saving it write a two-factor rule that computes a different number.
 *
 * Folding `multiply(a, b, c)` into `multiply(multiply(a, b), c)` keeps every operand, produces
 * exactly the shape the builder now emits, and shows the analyst what they have. The nesting is
 * left-associative, which is right for both functions and matches how the engine folds a list.
 *
 * This also covers operands that never came from the builder at all — a typology-JSON upload can
 * carry any number of them.
 */
export const migrateFuncValueArity = (funcValue, config) => {
  if (!funcValue || typeof funcValue !== 'object' || !funcValue.func)
    return funcValue;

  const migrateArg = (arg) =>
    arg?.valueSrc === 'func'
      ? { ...arg, value: migrateFuncValueArity(arg.value, config) }
      : arg;

  const declared = funcArgKeys(config, funcValue.func);
  const entries = Object.entries(funcValue.args || {});
  const surplus = entries
    .filter(
      ([key, arg]) =>
        !declared?.includes(key) && isNumberedArg(key) && hasValue(arg)
    )
    .sort(([a], [b]) => argIndex(a) - argIndex(b));

  // Only a two-operand function that numbers its own operands can absorb the surplus by nesting.
  if (
    !surplus.length ||
    declared.length !== 2 ||
    !declaresNumberedArgs(declared)
  ) {
    return {
      ...funcValue,
      args: Object.fromEntries(
        entries.map(([key, arg]) => [key, migrateArg(arg)])
      )
    };
  }

  const [first, second] = declared;
  const operands = declared
    .map((key) => funcValue.args[key])
    .filter(hasValue)
    .concat(surplus.map(([, arg]) => arg))
    .map(migrateArg);

  return operands.reduce((acc, operand) =>
    acc.func
      ? {
          func: funcValue.func,
          args: { [first]: { valueSrc: 'func', value: acc }, [second]: operand }
        }
      : { func: funcValue.func, args: { [first]: acc, [second]: operand } }
  );
};

/** Walks a plain-JS RAQB tree and folds every over-wide function into nested form. */
export const migrateFuncArity = (node, config) => {
  if (!node || typeof node !== 'object') return node;

  const next = { ...node };

  if (node.properties?.field && typeof node.properties.field === 'object') {
    next.properties = {
      ...node.properties,
      field: migrateFuncValueArity(node.properties.field, config)
    };
  }

  if (node.children1 && typeof node.children1 === 'object') {
    next.children1 = Array.isArray(node.children1)
      ? node.children1.map((child) => migrateFuncArity(child, config))
      : Object.entries(node.children1).reduce((acc, [key, child]) => {
          acc[key] = migrateFuncArity(child, config);
          return acc;
        }, {});
  }

  return next;
};
